/**
 * useDashboardState
 * Centralises all state, effects, and derived data for TelerivetDashboardSection.
 * Extracted to keep the component under 500 lines.
 */

import { useState, useEffect, useMemo, useCallback } from "react"
import { subDays, startOfDay, endOfDay, format } from "date-fns"
import { DateRange } from "react-day-picker"
import { usePage } from "@/components/page/hooks"

import { useAnalytics, type DashboardCommentItem } from "./useAnalytics"
import { useVariablesAnalytics, usePinnedVarsBreakdowns } from "./useVariablesAnalytics"
import { useSupabaseDashboardData, type DashboardContactSourceFilter, type DashboardAgent } from "./useSupabaseDashboardData"
import { useOpenAIUsage } from "./useOpenAIUsage"
import { TabId } from "../utils/constants"
import { getMessageChannelLabel } from "../utils/formatting"
import type { RawContact, RawMessage } from "../components/types"
import { DEFAULT_BUSINESS_HOURS, type BusinessHoursConfig } from "../utils/analyticsUtils"

const DASHBOARD_STORAGE_KEY = "dashboard-settings"

function loadDashboardSettings() {
  try {
    const saved = localStorage.getItem(DASHBOARD_STORAGE_KEY)
    return saved ? JSON.parse(saved) : {}
  } catch { return {} }
}

function saveDashboardSettings(settings: Record<string, any>) {
  try {
    const existing = loadDashboardSettings()
    localStorage.setItem(DASHBOARD_STORAGE_KEY, JSON.stringify({ ...existing, ...settings }))
  } catch {}
}

export function useDashboardState() {
  const { team } = usePage()
  const supabaseProject = useMemo(() => ({ id: "supabase", name: "Supabase" }), [])

  // Date range
  const [dateRange, setDateRange] = useState<DateRange>({
    from: startOfDay(subDays(new Date(), 29)),
    to: endOfDay(new Date())
  })

  // Instant dashboard filters
  const [messageDirectionFilter, setMessageDirectionFilter] = useState<'all' | 'incoming' | 'outgoing'>('all')
  const [handledByFilter, setHandledByFilter] = useState<'all' | 'bot' | 'human'>('all')
  const [escalationFilter, setEscalationFilter] = useState<'all' | 'escalated' | 'resolved' | 'non_escalated'>('all')
  const [contactSourceFilter, setContactSourceFilter] = useState<DashboardContactSourceFilter>('real_only')
  const [agentFilter, setAgentFilter] = useState<'all' | string>('all')
  const [channelFilter, setChannelFilter] = useState<'all' | string>('all')

  // Business-hours window for the moderator-response metric. Persisted per
  // browser like the pinned vars, since it is a team convention, not a per-view
  // filter someone re-picks each session.
  const [businessHours, setBusinessHoursState] = useState<BusinessHoursConfig>(() => {
    const saved = loadDashboardSettings()
    return saved.businessHours && typeof saved.businessHours === "object"
      ? { ...DEFAULT_BUSINESS_HOURS, ...saved.businessHours }
      : DEFAULT_BUSINESS_HOURS
  })
  const setBusinessHours = useCallback((cfg: BusinessHoursConfig) => {
    setBusinessHoursState(cfg)
    saveDashboardSettings({ businessHours: cfg })
  }, [])

  // UI state
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const [selectedVarKey, setSelectedVarKey] = useState<string>('')
  const [selectedVarValue, setSelectedVarValue] = useState<string>('')

  // Pinned variable keys + config. Persisted to localStorage so pins survive
  // reloads (per browser). Initialized lazily from saved settings.
  const [pinnedVarKeys, setPinnedVarKeys] = useState<string[]>(() => {
    const saved = loadDashboardSettings()
    return Array.isArray(saved.pinnedVarKeys) ? saved.pinnedVarKeys : []
  })
  const [pinnedVarConfigs, setPinnedVarConfigs] = useState<Record<string, any>>(() => {
    const saved = loadDashboardSettings()
    return (saved.pinnedVarConfigs && typeof saved.pinnedVarConfigs === "object") ? saved.pinnedVarConfigs : {}
  })
  const togglePinnedVar = useCallback((key: string) => {
    setPinnedVarKeys((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key])
  }, [])
  const updatePinnedVarConfig = useCallback((key: string, config: any) => {
    setPinnedVarConfigs((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...config } }))
  }, [])

  // Persist pins whenever they change.
  useEffect(() => {
    saveDashboardSettings({ pinnedVarKeys, pinnedVarConfigs })
  }, [pinnedVarKeys, pinnedVarConfigs])

  // Main data fetching
  const {
    contacts, messages: allMessages, agents, formFieldMeta, formRecordUpdatedAt,
    loading, error, loadingProgress, projectLoadProgress,
    truncated, maxMessageRows,
    fetchData: fetchSupabaseData, clearData: clearSupabaseData
  } = useSupabaseDashboardData(team?.id)

  // OpenAI usage
  const {
    adminKey: openaiAdminKey, setAdminKey: setOpenaiAdminKey,
    projects: openaiProjects, selectedProjectId: selectedOpenaiProject,
    setSelectedProjectId: setSelectedOpenaiProject,
    usageData: openaiUsage, fetchUsageData: fetchOpenAIUsage
  } = useOpenAIUsage()

  // Data tables and groups are deprecated in Supabase dashboard.
  const selectedDataTable = null
  const dataTableRows: any[] = []
  const dataTableFields: any[] = []
  const loadingDataRows = false
  const dataTableError = ""
  const dataTableLoadProgress = ""
  const dataTableChartField = ""
  const dataTableChartType = "bar"
  const dataTableViewMode = "table"
  const dataTableExcludeEmpty = false
  const dataTableSearchQuery = ""
  const dataTableChartData: any[] = []
  const chartableFields: any[] = []
  const fetchDataTableRows = async () => {}
  const loadDataTableRowsForAssistant = async () => ({ rows: [], fields: [] })
  const exportDataTableToCSV = () => {}
  const setDataTableChartField = () => {}
  const setDataTableChartType = () => {}
  const setDataTableViewMode = () => {}
  const setDataTableExcludeEmpty = () => {}
  const setDataTableSearchQuery = () => {}

  const selectedGroup = null
  const groupMembers: any[] = []
  const loadingGroupMembers = false
  const fetchGroups = async () => {}
  const fetchGroupMembers = async () => {}
  const loadGroupMembersForAssistant = async () => []
  const exportGroupMembers = () => {}

  // Loaded projects (legacy shape used by assistant context)
  const loadedProjects = useMemo(() => [supabaseProject], [supabaseProject])
  const viewedProjects = loadedProjects

  // ==================== EFFECTS ====================

  useEffect(() => {
    saveDashboardSettings({ projectIds: [supabaseProject.id] })
  }, [supabaseProject.id])

  // Auto-refresh whenever the query changes (team/date range)
  useEffect(() => {
    if (!team?.id) return
    fetchSupabaseData(dateRange, { contactSourceFilter })
  }, [team?.id, fetchSupabaseData, contactSourceFilter, dateRange.from?.getTime(), dateRange.to?.getTime()])

  // ==================== CALLBACKS ====================

  const handleFetchOpenAIUsage = useCallback(() => {
    fetchOpenAIUsage(openaiAdminKey, dateRange, selectedOpenaiProject)
  }, [fetchOpenAIUsage, openaiAdminKey, dateRange, selectedOpenaiProject])

  // Distinct channel labels present in the loaded data, for the channel filter.
  const availableChannels = useMemo(() => {
    const set = new Set<string>()
    allMessages.forEach((m) => set.add(getMessageChannelLabel(m)))
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [allMessages])

  const filteredMessages = useMemo(() => {
    return allMessages.filter((m) => {
      if (messageDirectionFilter !== 'all' && m.direction !== messageDirectionFilter) return false
      if (agentFilter !== 'all' && m.agent_id !== Number(agentFilter)) return false
      if (channelFilter !== 'all' && getMessageChannelLabel(m) !== channelFilter) return false
      return true
    })
  }, [allMessages, messageDirectionFilter, agentFilter, channelFilter])

  const contactEscalationStateById = useMemo(() => {
    const acc = new Map<string, { hasUnresolved: boolean; hasAnyResolved: boolean }>()
    const parseIdList = (value: unknown): string[] => {
      if (!Array.isArray(value)) return []
      return value.map((v) => String(v || "").trim()).filter(Boolean)
    }

    allMessages.forEach((m) => {
      // Mirror moderation: escalation state comes from incoming user/synthetic messages.
      if (m.direction !== 'incoming') return
      if (!m.contact_id) return
      const vars = m.vars || {}
      const activeIds = parseIdList(vars.active_flag_ids)
      const resolvedIds = parseIdList(vars.resolved_flag_ids)

      const hasFlagged =
        activeIds.length > 0 ||
        vars.high_risk_flag === 'flagged'
        || vars.low_confidence_flag === 'flagged'
        || vars.asked_human_flag === 'flagged'
      const hasResolved =
        resolvedIds.length > 0 ||
        vars.high_risk_flag === 'resolved'
        || vars.low_confidence_flag === 'resolved'
        || vars.asked_human_flag === 'resolved'

      if (!acc.has(m.contact_id)) acc.set(m.contact_id, { hasUnresolved: false, hasAnyResolved: false })
      const cur = acc.get(m.contact_id)!
      if (hasFlagged) cur.hasUnresolved = true
      if (hasResolved) cur.hasAnyResolved = true
    })

    const state = new Map<string, 'escalated' | 'resolved' | 'non_escalated'>()
    contacts.forEach((c) => {
      const info = acc.get(c.id)
      if (!info) {
        state.set(c.id, 'non_escalated')
        return
      }
      if (info.hasUnresolved) state.set(c.id, 'escalated')
      else if (info.hasAnyResolved) state.set(c.id, 'resolved')
      else state.set(c.id, 'non_escalated')
    })

    return state
  }, [allMessages, contacts])

  const filteredContacts = useMemo(() => {
    const byEscalation = contacts.filter((c) => {
      if (escalationFilter === 'all') return true
      return contactEscalationStateById.get(c.id) === escalationFilter
    })

    const byHandledBy = byEscalation.filter((c) => {
      if (handledByFilter === 'all') return true
      const handledBy = String(c.vars?.handled_by || 'bot').toLowerCase()
      return handledBy === handledByFilter
    })

    const contactIdsWithVisibleMessages = new Set(filteredMessages.map((m) => m.contact_id))
    // Keep contacts that either have a visible message OR carry captured form
    // values (form.*), so forms filled by contacts who didn't chat in-range still
    // count toward form-variable distributions.
    const hasFormValues = (c: typeof contacts[number]) =>
      Object.keys(c.vars || {}).some((k) => k.startsWith("form."))
    return byHandledBy.filter((c) => contactIdsWithVisibleMessages.has(c.id) || hasFormValues(c))
  }, [contacts, escalationFilter, contactEscalationStateById, handledByFilter, filteredMessages])

  const filteredContactIds = useMemo(() => new Set(filteredContacts.map((c) => c.id)), [filteredContacts])
  const visibleMessages = useMemo(
    () => filteredMessages.filter((m) => filteredContactIds.has(m.contact_id)),
    [filteredMessages, filteredContactIds]
  )

  const filteredGroups = useMemo(() => [], [])
  const filteredDataTables = useMemo(() => [], [])

  // Analytics
  const analyticsData = useAnalytics(filteredContacts, visibleMessages, dateRange, contactSourceFilter, businessHours)
  const commentsList = useMemo((): DashboardCommentItem[] => {
    const out: DashboardCommentItem[] = []
    const fromTs = dateRange.from ? startOfDay(dateRange.from).getTime() / 1000 : null
    const toTs = dateRange.to ? endOfDay(dateRange.to).getTime() / 1000 : null

    const messageContentById = new Map<string, string>()
    allMessages.forEach((message) => {
      const id = String(message.id || "").trim()
      if (!id) return
      messageContentById.set(id, message.content ?? "")
    })

    contacts.forEach((contact) => {
      // A contact can be present here purely because it holds form data — that
      // fetch path intentionally bypasses the real/synthetic type filter (see
      // RawContact.type doc comment) so simulation-captured forms still show in
      // the Variables tab. Comments don't get that exception: with "real
      // conversations only" selected, a comment left on a simulated
      // conversation should not surface just because the contact also happens
      // to carry form data.
      if (contactSourceFilter === "real_only" && contact.type === "synthetic") return
      if (contactSourceFilter === "synthetic_only" && contact.type !== "synthetic") return

      const raw = contact?.vars?.internal_comments
      if (!raw) return
      const parsed = typeof raw === "string"
        ? (() => { try { return JSON.parse(raw) } catch { return null } })()
        : raw
      const arr = Array.isArray(parsed) ? parsed : []

      arr.forEach((entry: any) => {
        const text = entry?.text ?? entry?.comment ?? ""
        const commenterName = entry?.author_name ?? entry?.commenter ?? ""
        const commenterId = String(entry?.author_id ?? entry?.commenter_id ?? "")
        const timestamp = Number(entry?.created_at ?? entry?.timestamp ?? 0)
        const messageId = entry?.message_id ? String(entry.message_id).trim() : undefined
        const associatedMessage = messageId ? (messageContentById.get(messageId) ?? "") : ""

        if (!text && !commenterName) return
        if (timestamp > 0 && fromTs != null && timestamp < fromTs) return
        if (timestamp > 0 && toTs != null && timestamp > toTs) return

        out.push({
          contactId: contact.id,
          contact,
          messageId,
          associatedMessage,
          commentText: text,
          commenterName,
          commenterId,
          timestamp,
          type: messageId ? "message" : "conversation",
        })
      })
    })

    return out.sort((a, b) => b.timestamp - a.timestamp)
  }, [contacts, allMessages, dateRange.from?.getTime(), dateRange.to?.getTime(), contactSourceFilter])
  const { varValueBreakdown, contactsWithVarValue } = useVariablesAnalytics(filteredContacts, visibleMessages, selectedVarKey, selectedVarValue)
  const pinnedBreakdowns = usePinnedVarsBreakdowns(filteredContacts, visibleMessages, pinnedVarKeys, pinnedVarConfigs)

  const dateRangeLabel = useMemo(() => {
    if (!dateRange.from || !dateRange.to) return 'All time'
    return `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`
  }, [dateRange])

  return {
    businessHours,
    setBusinessHours,

    // Workspace
    team,

    // API / Loading
    telerivetApiKey: "", loadingApiKey: false, error,

    // Query filters
    messageDirectionFilter, setMessageDirectionFilter,
    handledByFilter, setHandledByFilter,
    escalationFilter, setEscalationFilter,
    contactSourceFilter, setContactSourceFilter,
    agentFilter, setAgentFilter,
    channelFilter, setChannelFilter, availableChannels,
    agents,
    loadedProjects, viewedProjects,

    // Date range
    dateRange, setDateRange, dateRangeLabel,

    // Data loading
    contacts, allMessages,
    loading, loadingProgress, projectLoadProgress,
    truncated, maxMessageRows,
    clearAllData: clearSupabaseData,

    // Form field metadata (titles + option labels for form.* vars)
    formFieldMeta,
    formRecordUpdatedAt,

    // UI state
    activeTab, setActiveTab,
    selectedVarKey, setSelectedVarKey,
    selectedVarValue, setSelectedVarValue,

    // Pinned vars
    pinnedVarKeys, pinnedVarConfigs, togglePinnedVar, updatePinnedVarConfig,

    // Filtered data
    filteredContacts, filteredMessages: visibleMessages, filteredGroups, filteredDataTables,

    // Analytics
    ...analyticsData,
    commentsList,

    // Derived analytics from other hooks
    varValueBreakdown, contactsWithVarValue, pinnedBreakdowns,

    // Data tables
    selectedDataTable, dataTableRows, dataTableFields,
    loadingDataRows, dataTableError, dataTableLoadProgress,
    dataTableChartField, dataTableChartType, dataTableViewMode,
    dataTableExcludeEmpty, dataTableSearchQuery, dataTableChartData,
    chartableFields, fetchDataTableRows, loadDataTableRowsForAssistant,
    exportDataTableToCSV, setDataTableChartField, setDataTableChartType,
    setDataTableViewMode, setDataTableExcludeEmpty, setDataTableSearchQuery,

    // Groups
    selectedGroup, groupMembers, loadingGroupMembers,
    fetchGroups, fetchGroupMembers, loadGroupMembersForAssistant,
    exportGroupMembers,

    // OpenAI usage
    openaiAdminKey, setOpenaiAdminKey, openaiProjects,
    selectedOpenaiProject, setSelectedOpenaiProject,
    openaiUsage, handleFetchOpenAIUsage,
  }
}
